import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCartIcon, HeartIcon } from 'lucide-react';
import { Product } from '../../types';
import { Badge, getBadgeVariant } from '../ui/Badge';
import { Rating } from '../ui/Rating';
import { useCart } from '../../context/CartContext';
interface ProductCardProps {
  product: Product;
  className?: string;
}
export function ProductCard({
  product,
  className = ''
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uk-UA').format(price);
  };
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };
  return <motion.article whileHover={{
    y: -4
  }} transition={{
    duration: 0.2
  }} className={`group bg-white rounded-2xl hover:shadow-2xl hover:shadow-black/20 transition-all duration-300 overflow-hidden flex flex-col h-full ${className}`} data-id="element-262">
      <Link to={`/product/${product.id}`} className="block flex-1 flex flex-col" data-id="element-263">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-gray-50 flex-shrink-0 flex items-center justify-center" data-id="element-264">
          {product.image ? <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-265" /> : <div className="text-center p-8" data-id="element-266">
              <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 border border-gray-200" data-id="element-267"></div>
              <p className="text-xs text-gray-400" data-id="element-268">Зображення товару</p>
            </div>}

          {/* Badge */}
          {product.badge && <div className="absolute top-3 left-3" data-id="element-269">
              <Badge variant={getBadgeVariant(product.badge)} data-id="element-270">
                {product.badge}
              </Badge>
            </div>}

          {/* Quick Actions */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-271">
            <motion.button whileTap={{
            scale: 0.9
          }} className="p-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full shadow-lg hover:bg-gray-50 hover:border-gray-300 transition-colors" aria-label="Додати в обране" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-id="element-272">
              <HeartIcon className="w-4 h-4 text-gray-600 hover:text-red-500 transition-colors" data-id="element-273" />
            </motion.button>
          </div>

          {/* Out of Stock Overlay */}
          {!product.inStock && <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center" data-id="element-274">
              <span className="px-4 py-2 bg-gray-100 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg" data-id="element-275">
                Немає в наявності
              </span>
            </div>}
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1 bg-white" data-id="element-276">
          {/* Rating */}
          <div className="mb-3 h-5" data-id="element-277">
            <Rating value={product.rating} reviewCount={product.reviewCount} size="sm" data-id="element-278" />
          </div>

          {/* Name */}
          <h3 className="text-sm font-medium text-gray-900 mb-4 group-hover:text-gray-700 transition-colors flex-1 line-clamp-2 min-h-[2.5rem]" data-id="element-279">
            {product.name}
          </h3>

          {/* Price & Add to Cart */}
          <div className="mt-auto pt-3 border-t border-gray-100" data-id="element-280">
            <div className="flex items-baseline gap-2 mb-3" data-id="element-281">
              <span className="text-lg font-bold text-gray-900" data-id="element-282">
                {formatPrice(product.price)} ₴
              </span>
              {product.oldPrice && <span className="text-xs text-gray-400 line-through" data-id="element-283">
                  {formatPrice(product.oldPrice)} ₴
                </span>}
            </div>

            <div className="flex items-center justify-between gap-2" data-id="element-284">
              {product.inStock && <span className="text-xs text-emerald-600 font-medium" data-id="element-285">
                  В наявності
                </span>}

              {product.inStock && <motion.button whileTap={{
              scale: 0.95
            }} onClick={handleAddToCart} className="ml-auto p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm" aria-label="Додати в кошик" data-id="element-286">
                  <ShoppingCartIcon className="w-4 h-4" data-id="element-287" />
                </motion.button>}
            </div>
          </div>
        </div>
      </Link>
    </motion.article>;
}